"use client";

import classNames from "classnames";

import { useState } from "react";
import { usePathname } from "next/navigation";

import Multimedia from "@stadium/shared/src/components/shared/multimedia";
import LinkWithLang from "@components/swagmagic/dynamic/slices/client-link-with-lang";

import { isActiveNavLink } from "@helpers/swagmagic/header";

import { NavCategory } from "@components/swagmagic/header/type";

import { OTHER_LINK_TYPE } from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

interface HeaderNavProps {
  categories: NavCategory[];
  className?: string;
  onLinkClick?: () => void;
}

export default function HeaderNav({
  categories,
  className,
  onLinkClick,
}: Readonly<HeaderNavProps>) {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const routePathname = usePathname();
  const pathname = routePathname || "";
  // Get the active category (hovered or first one)
  const activeCategory = hoveredCategory
    ? categories.find((category) => category.label === hoveredCategory)
    : categories?.[0];

  // Get links and quickLinks from the active category
  const displayLinks = activeCategory?.links || [];
  const displayQuickLinks = activeCategory?.quickLinks || [];

  // Split displayLinks into two columns
  const column1 = displayLinks?.slice(0, 11) ?? [];
  const column2 = displayLinks?.slice(11) ?? [];
  // Get image directly from the active category
  const currentImage = activeCategory?.image?.url || "";
  const currentImageAlt = activeCategory?.label
    ? `${activeCategory.label} category image`
    : "Category image";

  // Handler for resetting category hover state
  const handleMenuLeave = () => {
    setHoveredCategory(null);
  };

  return (
    <section className={classNames(styles.section, className)}>
      <nav
        className={styles.menuContainer}
        onMouseLeave={handleMenuLeave}
        aria-label="Shop by categories navigation"
      >
        <div className={styles.menuContentHolder}>
          <div className={styles.sidebar}>
            <ul className={styles.categoryList}>
              {categories.map((category, index) => (
                <li
                  key={category.id || category.label || index}
                  className={`${styles.categoryItem} ${
                    category.label === (hoveredCategory || categories[0]?.label)
                      ? styles.categoryItemActive
                      : ""
                  }`}
                >
                  <LinkWithLang
                    type={category.linkType || OTHER_LINK_TYPE}
                    data={category}
                    className={styles.categoryLink}
                    onClick={onLinkClick}
                    onMouseEnter={() => setHoveredCategory(category.label)}
                  >
                    {category.label}
                  </LinkWithLang>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.content}>
            <div className={styles.contentInner}>
              <div className={styles.linksGrid}>
                {/* Column 1 */}
                {column1.length > 0 && (
                  <ul className={styles.linksColumn}>
                    {column1.map((link, index) => (
                      <li key={link.id || link.label || index} className={styles.linkItem}>
                        <LinkWithLang
                          type={link.linkType || OTHER_LINK_TYPE}
                          data={link}
                          className={styles.link}
                          onClick={onLinkClick}
                          activeClassName={(linkUrl) =>
                            isActiveNavLink(pathname, linkUrl) ? styles.activeCategoryItem : ""
                          }
                        >
                          {link.label}
                        </LinkWithLang>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Column 2 */}
                <div className={styles.column2Wrapper}>
                  {column2.length > 0 && (
                    <ul className={styles.linksColumn}>
                      {column2.map((link, index) => (
                        <li key={link.id || link.label || index} className={styles.linkItem}>
                          <LinkWithLang
                            type={link.linkType || OTHER_LINK_TYPE}
                            data={link}
                            className={styles.link}
                            onClick={onLinkClick}
                            activeClassName={(linkUrl) =>
                              isActiveNavLink(pathname, linkUrl) ? styles.activeCategoryItem : ""
                            }
                          >
                            {link.label}
                          </LinkWithLang>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Quick Links */}
                  {displayQuickLinks.length > 0 && (
                    <div className={styles.quickLinksSection}>
                      <h3 className={styles.quickLinksHeading}>Quick Links</h3>
                      <ul className={styles.linksColumn}>
                        {displayQuickLinks.map((link, index) => (
                          <li key={link.id || link.label || index} className={styles.linkItem}>
                            <LinkWithLang
                              type={link.linkType || OTHER_LINK_TYPE}
                              data={link}
                              className={styles.link}
                              onClick={onLinkClick}
                              query={link.query}
                            >
                              {link.label}
                            </LinkWithLang>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {activeCategory?.viewAllButtonText && activeCategory?.viewAllButtonUrl && (
                    <LinkWithLang
                      type={OTHER_LINK_TYPE}
                      data={{ href: activeCategory.viewAllButtonUrl }}
                      className={styles.viewAllButton}
                      onClick={onLinkClick}
                    >
                      {activeCategory.viewAllButtonText}
                    </LinkWithLang>
                  )}
                </div>
              </div>

              {/* Image on Right */}
              <div className={styles.imageWrapper}>
                <div className={styles.imageContainer}>
                  <Multimedia
                    type="image"
                    src={currentImage || ""}
                    alt={currentImageAlt}
                    className={styles.image}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </section>
  );
}
